// org-roam capabilities for organice: the pure half.
//
// org-roam (https://github.com/org-roam/org-roam) keeps a SQLite cache of every
// node in a directory of Org files, where a *node* is any headline - or the file
// itself - carrying an `:ID:` property. Everything it does well follows from
// that one index: find a note by name, jump through an `id:` link, insert a link
// to a note you have not opened.
//
// organice is a browser app with no SQLite and no filesystem, so this module is
// the index instead: pure functions that turn Org text into nodes and links, and
// query the result. Nothing here touches the network or storage - lib/
// org_roam_db.js persists it and the components drive it - so all of it
// unit-tests without rendering, without a backend and without a clock.
//
// Deliberately a subset of org-roam's schema. It keeps what the four requested
// capabilities need - id, file, title, level, line, outline path, tags, aliases,
// and the links between nodes - and drops what only matters inside Emacs:
// citations, refs, todo/priority/scheduled/deadline mirrors, file hashes and
// atime/mtime. Those are recoverable from the file at any time and would cost
// storage on every sync (see org_roam_db.js on the localStorage budget).

// Where the notes live. org-roam calls this `org-roam-directory`; here it is the
// `roamDirectory` setting, and everything at or below it is scanned.
export const DEFAULT_ROAM_DIRECTORY = '/roam';

// [[id:UUID][description]] and the bare [[id:UUID]] form. The id itself is
// deliberately loose: org-id will happily use a uuid, a timestamp or a
// user-supplied string, and rejecting anything that is not a uuid would silently
// break hand-made ids.
export const ID_LINK_REGEX = /\[\[id:([^\]]+?)\](?:\[([^\]]*?)\])?\]/g;

/** A single `id:` link target, or null - for the click handler on one link. */
export const idFromLinkUri = (uri) => {
  const match = /^id:(.+)$/.exec(`${uri || ''}`.trim());
  return match ? match[1].trim() : null;
};

// How much of a link's surrounding line is kept for the backlink list. Long
// enough to be a sentence, short enough that the index stays small (see
// org_roam_db.js on the localStorage budget).
export const MAX_CONTEXT = 200;

const truncate = (text, max) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/**
 * Org link markup out of a line meant for reading: `[[target][text]]` becomes
 * `text`, and a bare `[[target]]` becomes the target.
 *
 * A backlink's context exists to be read. Left raw it is mostly brackets and
 * uuids - "See [[id:9f2c…][Anki]] and…" - which is exactly the noise org-roam's
 * own backlink buffer hides by fontifying. Stored stripped rather than stripped
 * at render, because the raw form has no other use here.
 */
export const stripOrgLinks = (text) =>
  `${text || ''}`
    .replace(/\[\[([^[\]]+?)\]\[([^[\]]*?)\]\]/g, '$2')
    .replace(/\[\[([^[\]]+?)\]\]/g, '$1');

const HEADLINE_REGEX = /^(\*+)\s+(.*)$/;
const PROPERTY_REGEX = /^\s*:([^\s:]+):\s*(.*)$/;
const DRAWER_START_REGEX = /^\s*:PROPERTIES:\s*$/i;
const DRAWER_END_REGEX = /^\s*:END:\s*$/i;
const KEYWORD_REGEX = /^\s*#\+([A-Za-z_]+):\s*(.*)$/;

/** Trailing `:a:b:` tags on a headline, and the headline text without them. */
const splitHeadlineTags = (text) => {
  const match = /^(.*?)\s+:((?:[^\s:]+:)+)\s*$/.exec(text);
  if (!match) return { title: text.trim(), tags: [] };

  return { title: match[1].trim(), tags: match[2].split(':').filter(Boolean) };
};

const stripTodoKeyword = (text, todoKeywords) => {
  const match = /^([A-Z][A-Z0-9_-]*)\s+(.*)$/.exec(text);
  return match && todoKeywords.includes(match[1]) ? match[2] : text;
};

/**
 * org-roam stores ROAM_ALIASES as a space-separated list where multi-word
 * aliases are double-quoted (`org-entry-get` + `split-string-and-unquote`).
 * Splitting on whitespace alone would shatter every multi-word alias, which are
 * the ones people actually add.
 */
export const parseAliases = (value) => {
  const raw = `${value || ''}`.trim();
  if (!raw) return [];

  const aliases = [];
  const pattern = /"([^"]*)"|(\S+)/g;
  let match = pattern.exec(raw);

  while (match) {
    const alias = (match[1] !== undefined ? match[1] : match[2]).trim();
    if (alias) aliases.push(alias);
    match = pattern.exec(raw);
  }

  return aliases;
};

const parseFiletags = (value) =>
  `${value || ''}`
    .split(':')
    .map((tag) => tag.trim())
    .filter(Boolean);

/**
 * Every node and `id:` link in one Org file.
 *
 * A node is the file itself (when a top-level property drawer carries an `:ID:`)
 * or any headline whose own drawer carries one - exactly org-roam's rule. A
 * headline without an `:ID:` is not a node, but its content still belongs to the
 * nearest enclosing node, which is what makes a link's `source` correct.
 *
 * `line` is 1-based so it can be shown to a human and used as an anchor.
 * Returns `{ file, title, nodes, links }`; `title` is the file's `#+title:`.
 */
export const parseRoamFile = (path, contents, todoKeywords = ['TODO', 'DONE']) => {
  const lines = `${contents || ''}`.split('\n');

  const nodes = [];
  const links = [];

  let fileTitle = '';
  let fileTags = [];
  let sawHeadline = false;

  // The headline stack, for outline paths. Each entry is { level, title }.
  const stack = [];
  // Set while reading a property drawer; collects until :END:.
  let drawer = null;

  const fileNodeIndex = () => nodes.findIndex((node) => node.level === 0);

  const collectLinks = (line, lineNumber) => {
    ID_LINK_REGEX.lastIndex = 0;
    let link = ID_LINK_REGEX.exec(line);

    while (link) {
      // `source` is filled in below, once every node's line is known.
      links.push({
        source: null,
        dest: link[1].trim(),
        description: (link[2] || '').trim(),
        file: path,
        line: lineNumber,
        // The sentence the link sits in. org-roam's backlink buffer shows this,
        // and it is most of why backlinks are worth having: "12 notes link here"
        // is trivia, "…which is why atomicity matters" is the actual thought.
        // Capped because one pathological line should not dominate the index.
        context: truncate(stripOrgLinks(line.trim()), MAX_CONTEXT),
      });
      link = ID_LINK_REGEX.exec(line);
    }
  };

  lines.forEach((line, index) => {
    const lineNumber = index + 1;

    if (drawer) {
      if (DRAWER_END_REGEX.test(line)) {
        const id = drawer.properties.ID;

        if (id) {
          nodes.push({
            id: id.trim(),
            file: path,
            title: drawer.owner ? drawer.owner.title : fileTitle,
            level: drawer.owner ? drawer.owner.level : 0,
            line: drawer.owner ? drawer.owner.line : 1,
            olp: drawer.owner ? drawer.owner.olp : [],
            tags: drawer.owner ? drawer.owner.tags : fileTags,
            aliases: parseAliases(drawer.properties.ROAM_ALIASES),
          });
        }

        drawer = null;
      } else {
        const property = PROPERTY_REGEX.exec(line);
        if (property) drawer.properties[property[1].toUpperCase()] = property[2];
      }
      return;
    }

    const headline = HEADLINE_REGEX.exec(line);
    if (headline) {
      sawHeadline = true;

      const level = headline[1].length;
      const { title, tags } = splitHeadlineTags(headline[2]);

      while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
      const olp = stack.map((entry) => entry.title);
      stack.push({ level, title });

      // What the next drawer would belong to, if one follows this headline.
      stack[stack.length - 1].pending = {
        title: stripTodoKeyword(title, todoKeywords),
        level,
        line: lineNumber,
        olp,
        tags,
      };

      // A link can sit in the headline text itself, so scan it too.
      collectLinks(line, lineNumber);
      return;
    }

    if (DRAWER_START_REGEX.test(line)) {
      const owner = stack.length ? stack[stack.length - 1].pending : null;
      // A drawer before the first headline belongs to the file itself.
      drawer = { owner: sawHeadline ? owner : null, properties: {} };
      return;
    }

    const keyword = KEYWORD_REGEX.exec(line);
    if (keyword && !sawHeadline) {
      const name = keyword[1].toUpperCase();
      if (name === 'TITLE') {
        fileTitle = keyword[2].trim();
        // A #+title: below the drawer still names the file node above it.
        const fileIndex = fileNodeIndex();
        if (fileIndex !== -1 && !nodes[fileIndex].title) nodes[fileIndex].title = fileTitle;
      } else if (name === 'FILETAGS') {
        fileTags = parseFiletags(keyword[2]);
        const fileIndex = fileNodeIndex();
        if (fileIndex !== -1 && !nodes[fileIndex].tags.length) nodes[fileIndex].tags = fileTags;
      }
      return;
    }

    collectLinks(line, lineNumber);
  });

  // A file whose node had no #+title: when its drawer closed is named after its
  // own filename - the same fallback org-roam shows in its completions.
  const fileIndex = fileNodeIndex();
  if (fileIndex !== -1 && !nodes[fileIndex].title) {
    nodes[fileIndex].title = fileTitle || basename(path);
  }

  // A link belongs to the innermost node open at that point, which - because
  // nodes appear in document order - is simply the node with the greatest line
  // at or above the link's own. Resolving it here rather than while scanning is
  // what makes a link in a headline belong to *that* headline's node: the
  // headline is seen before its own :ID: drawer is.
  const byLine = nodes.slice().sort((a, b) => a.line - b.line);
  links.forEach((link) => {
    let source = null;
    byLine.forEach((node) => {
      if (node.line <= link.line) source = node.id;
    });
    link.source = source;
  });

  return { file: path, title: fileTitle || basename(path), nodes, links };
};

/** `/roam/notes/foo.org` -> `foo`. */
export const basename = (path) => {
  const name = `${path || ''}`.split('/').pop() || '';
  return name.replace(/\.org$/i, '');
};

/**
 * Whether `path` sits inside `directory` - "does this file belong in the index
 * at all".
 *
 * The index is *defined* as the contents of the roam directory: a full sync
 * rebuilds it from exactly that and keeps nothing else (see `syncRoamDatabase`).
 * So a file outside it has to stay out. Indexing one anyway would work until the
 * next sync and then quietly stop, which is the worst of the three outcomes.
 */
export const isUnderDirectory = (path, directory) => {
  const file = `${path || ''}`.trim();
  const dir = `${directory || ''}`.trim().replace(/\/+$/, '');
  if (!file) return false;

  // A roam directory of '/' (or empty) is the whole tree, which every absolute
  // path is under and no relative one is.
  return dir ? file.startsWith(`${dir}/`) : file.startsWith('/');
};

/** An empty database, in the shape everything below expects. */
export const emptyDatabase = () => ({ nodes: [], links: [], files: [], syncedAt: null });

const normalizeNode = (node) => ({
  id: `${node.id}`,
  file: `${node.file}`,
  title: `${node.title || ''}`,
  level: Number.isFinite(node.level) ? node.level : 0,
  line: Number.isFinite(node.line) ? node.line : 1,
  olp: Array.isArray(node.olp) ? node.olp.map(String) : [],
  tags: Array.isArray(node.tags) ? node.tags.map(String) : [],
  aliases: Array.isArray(node.aliases) ? node.aliases.map(String) : [],
});

/**
 * Whatever was read back out of storage, in the shape this module works on.
 *
 * Nodes without an id are dropped and duplicate ids keep the first seen: a
 * duplicated `:ID:` is a real thing that happens when a note is copied, and it
 * must not take the database down.
 */
export const normalizeDatabase = (db) => {
  const seen = new Set();
  const nodes = [];

  (Array.isArray(db && db.nodes) ? db.nodes : []).forEach((node) => {
    if (!node || !node.id || seen.has(`${node.id}`)) return;
    seen.add(`${node.id}`);
    nodes.push(normalizeNode(node));
  });

  const links = (Array.isArray(db && db.links) ? db.links : [])
    .filter((link) => link && link.dest)
    .map((link) => ({
      source: link.source ? `${link.source}` : null,
      dest: `${link.dest}`,
      description: `${link.description || ''}`,
      file: `${link.file || ''}`,
      line: Number.isFinite(link.line) ? link.line : 1,
      context: truncate(`${link.context || ''}`, MAX_CONTEXT),
    }));

  const files = Array.isArray(db && db.files) ? db.files.map(String) : [];

  return {
    nodes,
    links,
    files: [...new Set(files)].sort(),
    syncedAt: typeof (db && db.syncedAt) === 'number' ? db.syncedAt : null,
  };
};

/**
 * Replace everything the database knows about one file (org-roam's own
 * `org-roam-db-update-file`: delete the file's rows, re-insert them).
 *
 * This is what makes an incremental re-sync possible without rescanning
 * everything - the "and then when changing something" half of a db-sync.
 */
export const mergeFile = (db, parsed) => {
  const current = normalizeDatabase(db);
  const path = parsed.file;

  return normalizeDatabase({
    ...current,
    nodes: [...current.nodes.filter((node) => node.file !== path), ...parsed.nodes],
    links: [...current.links.filter((link) => link.file !== path), ...parsed.links],
    files: [...current.files, path],
  });
};

/** Forget a file entirely - it was deleted, or moved out of the roam directory. */
export const removeFile = (db, path) => {
  const current = normalizeDatabase(db);

  return normalizeDatabase({
    ...current,
    nodes: current.nodes.filter((node) => node.file !== path),
    links: current.links.filter((link) => link.file !== path),
    files: current.files.filter((file) => file !== path),
  });
};

export const nodeById = (db, id) => {
  if (!id) return null;
  const target = `${id}`.trim();

  return normalizeDatabase(db).nodes.find((node) => node.id === target) || null;
};

/** Every name a node answers to: its title first, then its aliases. */
export const nodeNames = (node) => [node.title, ...(node.aliases || [])].filter(Boolean);

// Ranked rather than filtered, so the obvious answer is first: an exact name
// beats a prefix, a prefix beats a substring, and a hit on the real title beats
// the same hit on an alias. Everything else is alphabetical, so the list does
// not reshuffle for reasons the reader cannot see.
const scoreNode = (node, needle) => {
  let best = 0;

  nodeNames(node).forEach((name, index) => {
    const haystack = name.toLowerCase();
    const isAlias = index > 0;

    let score = 0;
    if (haystack === needle) score = 100;
    else if (haystack.startsWith(needle)) score = 70;
    else if (haystack.includes(needle)) score = 40;

    if (score && isAlias) score -= 5;
    best = Math.max(best, score);
  });

  return best;
};

/**
 * Nodes matching `query` by title or alias, best first (org-roam-node-find).
 *
 * An empty query returns everything, alphabetically - that is the state the
 * finder opens in, and showing nothing until the first keystroke would hide the
 * database from someone who does not yet know what is in it.
 */
export const findNodes = (db, query, limit = 50) => {
  const nodes = normalizeDatabase(db).nodes;
  const needle = `${query || ''}`.trim().toLowerCase();

  const byTitle = (a, b) =>
    a.title.toLowerCase() < b.title.toLowerCase()
      ? -1
      : a.title.toLowerCase() > b.title.toLowerCase()
      ? 1
      : 0;

  if (!needle) return nodes.slice().sort(byTitle).slice(0, limit);

  return nodes
    .map((node) => ({ node, score: scoreNode(node, needle) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || byTitle(a.node, b.node))
    .map((entry) => entry.node)
    .slice(0, limit);
};

/** The link text org-roam-node-insert writes: `[[id:UUID][Title]]`. */
export const formatIdLink = (node, description) => {
  if (!node || !node.id) return '';
  const text = `${description || node.title || ''}`.trim();

  return text ? `[[id:${node.id}][${text}]]` : `[[id:${node.id}]]`;
};

/** Everything that links *to* this node, with the node each link came from. */
export const backlinksFor = (db, id) => {
  const current = normalizeDatabase(db);
  const target = `${id}`.trim();
  const byId = new Map(current.nodes.map((node) => [node.id, node]));

  return current.links
    .filter((link) => link.dest === target)
    .map((link) => ({ ...link, sourceNode: link.source ? byId.get(link.source) || null : null }));
};

/**
 * How many links point at each node, keyed by id - one pass rather than a scan
 * per row, since the finder shows a count on every result.
 *
 * A node with no backlinks is absent rather than zero; the caller treats a
 * missing key as none, and storing thousands of zeroes would be wasteful.
 */
export const backlinkCounts = (db) => {
  const counts = {};

  normalizeDatabase(db).links.forEach((link) => {
    counts[link.dest] = (counts[link.dest] || 0) + 1;
  });

  return counts;
};

/** Every node the index has for one file, in the order they appear in it. */
export const nodesInFile = (db, path) => {
  const target = `${path || ''}`;

  return normalizeDatabase(db)
    .nodes.filter((node) => node.file === target)
    .sort((a, b) => a.line - b.line);
};

/**
 * What links into a file, grouped by the node inside it that is being linked to.
 *
 * A file usually holds several nodes - itself plus a node per headline that has
 * an :ID: - and "what links here" for a file is only meaningful if it says which
 * of them each link was aimed at. Nodes with no inbound links are left out; the
 * caller can still tell "nothing links to this file" from "this file has no
 * nodes at all" by asking nodesInFile.
 *
 * Links from the file to itself are kept: a note that refers back to its own
 * headline is a real thing, and hiding it would misreport the count.
 */
export const fileBacklinks = (db, path) => {
  const current = normalizeDatabase(db);

  return nodesInFile(current, path)
    .map((node) => ({ node, backlinks: backlinksFor(current, node.id) }))
    .filter((group) => group.backlinks.length > 0);
};

/**
 * What a file links *out* to, grouped by the node inside it that does the linking.
 *
 * The mirror of fileBacklinks, and deliberately not symmetrical in one respect:
 * a destination the index has never heard of is still returned, with a null
 * `destNode`. Those are the file's broken links, and a view that silently
 * dropped them would hide the one thing worth seeing.
 *
 * Links written above the first node in the file have no source node; they are
 * grouped under a null node rather than discarded, and sorted first, since that
 * is where they appear in the file.
 */
export const fileForwardLinks = (db, path) => {
  const current = normalizeDatabase(db);
  const target = `${path || ''}`;
  const byId = new Map(current.nodes.map((node) => [node.id, node]));

  const groups = new Map();
  current.links
    .filter((link) => link.file === target)
    .forEach((link) => {
      const key = link.source || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ ...link, destNode: byId.get(link.dest) || null });
    });

  return [...groups.entries()]
    .map(([key, links]) => ({ node: key ? byId.get(key) || null : null, links }))
    .sort((a, b) => (a.node ? a.node.line : 0) - (b.node ? b.node.line : 0));
};

/** Links pointing at an id the database has never seen - broken, or not synced yet. */
export const danglingLinks = (db) => {
  const current = normalizeDatabase(db);
  const ids = new Set(current.nodes.map((node) => node.id));

  return current.links.filter((link) => !ids.has(link.dest));
};

export const databaseStats = (db) => {
  const current = normalizeDatabase(db);

  return {
    nodes: current.nodes.length,
    files: current.files.length,
    links: current.links.length,
    dangling: danglingLinks(current).length,
    syncedAt: current.syncedAt,
  };
};
